import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
    Select as ShadcnSelect,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Checkbox as ShadcnCheckbox } from "@/components/ui/checkbox";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Drop-in shim for Fluent UI's `Dropdown`. Single-select routes through
 * shadcn `Select`; multi-select (`multiSelect={true}`) renders a Popover
 * with a checkbox list. The `(ev, option, index) => void` change callback
 * is preserved on both paths so existing call sites keep working.
 *
 * Fluent-only props (`styles`, `errorMessage`, `componentRef`, render hooks)
 * follow the same drop policy as the other shims.
 */

export interface IDropdownOption {
  key: string | number;
  /** Fluent UI types `text` as required — keep parity so existing call sites
   *  that call `option.text.localeCompare(...)` etc. type-check. */
  text: string;
  itemType?: any;
  disabled?: boolean;
  hidden?: boolean;
  selected?: boolean;
  data?: any;
  index?: number;
  ariaLabel?: string;
  title?: string;
  [key: string]: any;
}

type FluentDropdownChange = (
  event: React.FormEvent<HTMLDivElement>,
  option?: IDropdownOption,
  index?: number,
) => void;

export interface FluentDropdownProps {
  label?: React.ReactNode;
  placeholder?: string;
  options: IDropdownOption[];
  selectedKey?: string | number | null;
  selectedKeys?: Array<string | number> | null;
  defaultSelectedKey?: string | number | null;
  defaultSelectedKeys?: Array<string | number> | null;
  multiSelect?: boolean;
  disabled?: boolean;
  required?: boolean;
  errorMessage?: React.ReactNode;
  className?: string;
  dropdownWidth?: number | "auto";
  ariaLabel?: string;
  title?: string;
  id?: string;
  onChange?: FluentDropdownChange;
  // Drops:
  styles?: any;
  componentRef?: any;
  onRenderTitle?: (...args: any[]) => any;
  onRenderOption?: (...args: any[]) => any;
  onRenderItem?: (...args: any[]) => any;
  onRenderCaretDown?: (...args: any[]) => any;
  onRenderLabel?: (...args: any[]) => any;
  onRenderPlaceholder?: (...args: any[]) => any;
  onRenderContainer?: (...args: any[]) => any;
  onRenderList?: (...args: any[]) => any;
  onDismiss?: () => void;
  notifyOnReselect?: boolean;
  calloutProps?: any;
  panelProps?: any;
  responsiveMode?: any;
  multiSelectDelimiter?: string;
  keytipProps?: any;
  /** Standard React inline style — applied to the wrapper. */
  style?: React.CSSProperties;
}

const VISIBLE = (o: IDropdownOption) => !o.hidden && o.itemType !== 1; // 1 = Header in Fluent

function findOption(
  options: IDropdownOption[],
  key: string | number | null | undefined,
): { option?: IDropdownOption; index: number } {
  if (key === null || key === undefined) return { index: -1 };
  const idx = options.findIndex((o) => o.key === key);
  return idx >= 0 ? { option: options[idx], index: idx } : { index: -1 };
}

const Wrapper: React.FC<{
  label?: React.ReactNode;
  required?: boolean;
  errorMessage?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  htmlFor?: string;
  children: React.ReactNode;
}> = ({ label, required, errorMessage, disabled, className, style, htmlFor, children }) => (
  <div className={cn("flex flex-col gap-1.5", className)} style={style}>
    {label != null && label !== false && label !== "" && (
      <label
        htmlFor={htmlFor}
        className={cn("text-sm font-medium leading-none", disabled && "opacity-50")}
      >
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
    )}
    {children}
    {errorMessage != null && errorMessage !== false && errorMessage !== "" && (
      <p className="text-xs text-destructive">{errorMessage}</p>
    )}
  </div>
);

export const Dropdown = React.forwardRef<HTMLDivElement, FluentDropdownProps>(
  function Dropdown(props, _ref) {
    const reactId = React.useId();
    const fieldId = props.id ?? reactId;

    if (props.multiSelect) {
      return <MultiSelectDropdown {...props} fieldId={fieldId} />;
    }
    return <SingleSelectDropdown {...props} fieldId={fieldId} />;
  },
);
Dropdown.displayName = "Dropdown";

// ─── Single-select ─────────────────────────────────────────────────────────────

// Radix's Select treats `value=""` as "no selection / show placeholder", so it
// throws when a SelectItem renders with an empty string. Fluent Dropdowns
// commonly use an option with `key: ''` as a "—" / "no selection" entry. To
// keep that idiom working we substitute a sentinel for empty keys at the
// radix boundary and translate back when the change fires.
const EMPTY_KEY_SENTINEL = "__compat_empty_key__";

const toSelectValue = (k: string | number | null | undefined): string | undefined => {
  if (k === null || k === undefined) return undefined;
  const s = String(k);
  return s === "" ? EMPTY_KEY_SENTINEL : s;
};

const fromSelectValue = (v: string): string =>
  v === EMPTY_KEY_SENTINEL ? "" : v;

function SingleSelectDropdown(props: FluentDropdownProps & { fieldId: string }) {
  const {
    label,
    placeholder,
    options,
    selectedKey,
    defaultSelectedKey,
    disabled,
    required,
    errorMessage,
    className,
    ariaLabel,
    title,
    onChange,
    style,
    fieldId,
    dropdownWidth,
  } = props;

  const visibleOptions = options.filter(VISIBLE);

  const handleValueChange = (value: string) => {
    const actualKey = fromSelectValue(value);
    const { option, index } = findOption(options, actualKey);
    if (option) {
      const synthetic = { target: {} } as unknown as React.FormEvent<HTMLDivElement>;
      onChange?.(synthetic, option, index);
    }
  };

  // Shadcn Select uses string values, so map all keys to strings — and route
  // empty keys through the sentinel so radix doesn't reject them.
  const stringValue = toSelectValue(selectedKey);
  const stringDefault = toSelectValue(defaultSelectedKey);

  return (
    <Wrapper
      label={label}
      required={required}
      errorMessage={errorMessage}
      disabled={disabled}
      className={className}
      style={style}
      htmlFor={fieldId}
    >
      <ShadcnSelect
        value={stringValue}
        defaultValue={stringDefault}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          id={fieldId}
          aria-label={ariaLabel}
          title={title}
          className={cn(
            errorMessage && "border-destructive focus:ring-destructive",
          )}
          style={
            dropdownWidth && dropdownWidth !== "auto"
              ? { width: dropdownWidth }
              : undefined
          }
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {visibleOptions.map((opt) => {
            const radixValue = toSelectValue(opt.key) ?? EMPTY_KEY_SENTINEL;
            return (
              <SelectItem
                key={String(opt.key)}
                value={radixValue}
                disabled={opt.disabled}
              >
                {opt.text ?? String(opt.key)}
              </SelectItem>
            );
          })}
        </SelectContent>
      </ShadcnSelect>
    </Wrapper>
  );
}

// ─── Multi-select (popover + checkbox list) ────────────────────────────────────

function MultiSelectDropdown(props: FluentDropdownProps & { fieldId: string }) {
  const {
    label,
    placeholder,
    options,
    selectedKeys,
    defaultSelectedKeys,
    disabled,
    required,
    errorMessage,
    className,
    ariaLabel,
    title,
    onChange,
    style,
    fieldId,
    dropdownWidth,
    multiSelectDelimiter = ", ",
  } = props;

  // Internal state for uncontrolled usage.
  const [internalKeys, setInternalKeys] = React.useState<Array<string | number>>(
    defaultSelectedKeys ?? [],
  );
  const isControlled = selectedKeys !== undefined && selectedKeys !== null;
  const currentKeys = isControlled ? (selectedKeys as Array<string | number>) : internalKeys;

  const visibleOptions = options.filter(VISIBLE);

  const labelFor = (k: string | number): string => {
    const opt = options.find((o) => o.key === k);
    return opt?.text ?? String(k);
  };
  const triggerLabel = currentKeys.length === 0
    ? (placeholder ?? "")
    : currentKeys.map(labelFor).join(multiSelectDelimiter);

  const toggle = (key: string | number) => {
    const isSelected = currentKeys.includes(key);
    const nextKeys = isSelected
      ? currentKeys.filter((k) => k !== key)
      : [...currentKeys, key];
    if (!isControlled) setInternalKeys(nextKeys);
    const { option, index } = findOption(options, key);
    if (option) {
      const synthetic = {
        target: {},
      } as unknown as React.FormEvent<HTMLDivElement>;
      // Fluent's API marks `option.selected` to indicate the new state of the
      // toggled option, so call sites that read it for diffs see the truth.
      onChange?.(synthetic, { ...option, selected: !isSelected }, index);
    }
  };

  return (
    <Wrapper
      label={label}
      required={required}
      errorMessage={errorMessage}
      disabled={disabled}
      className={className}
      style={style}
      htmlFor={fieldId}
    >
      <Popover>
        <PopoverTrigger
          id={fieldId}
          aria-label={ariaLabel}
          title={title}
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
            errorMessage && "border-destructive focus-visible:ring-destructive/20",
          )}
          style={
            dropdownWidth && dropdownWidth !== "auto"
              ? { width: dropdownWidth }
              : undefined
          }
        >
          <span
            className={cn(
              "line-clamp-1 text-left",
              currentKeys.length === 0 && "text-muted-foreground",
            )}
          >
            {triggerLabel || placeholder || "—"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
          <div className="max-h-72 overflow-y-auto">
            {visibleOptions.map((opt) => {
              const checked = currentKeys.includes(opt.key);
              const itemId = `${fieldId}-opt-${String(opt.key)}`;
              return (
                <label
                  key={String(opt.key)}
                  htmlFor={itemId}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground",
                    opt.disabled && "pointer-events-none opacity-50",
                  )}
                >
                  <ShadcnCheckbox
                    id={itemId}
                    checked={checked}
                    disabled={opt.disabled}
                    onCheckedChange={() => toggle(opt.key)}
                  />
                  <span className="flex-1 truncate">{opt.text ?? String(opt.key)}</span>
                </label>
              );
            })}
            {visibleOptions.length === 0 && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">No options</div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </Wrapper>
  );
}
